import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DeviceState as DeviceStateEnum, DeviceType, Location, Room } from '@rocksoft/locations-sdk';
import { Device } from '@rocksoft/shared';
import { CategoryStoreSelectors } from '../category';
import { DevicesRouterStoreSelectors, LocationsRouterStoreSelectors } from '../router';
import * as fromReducer from './device.reducer';
import { LocationSubset } from './models/location-subset';
import { DeviceState } from './device.state';

export const selectDeviceState = createFeatureSelector<DeviceState>(fromReducer.DEVICE_FEATURE_KEY);

// Entity selectors composed with feature state so they receive the entity slice, not root state.
// Using the reducer's selectors directly would receive root state and throw (state.ids is undefined).
export const selectDeviceEntities = createSelector(selectDeviceState, fromReducer.selectDevices);
export const selectAll = createSelector(selectDeviceState, fromReducer.selectAllDevices);

// ============================================================================
// LOAD FLAG SELECTORS
// ============================================================================

export const selectHasLoadedAnyLocationsDevice = createSelector(
  selectDeviceState,
  (state) => state.hasLoadedAnyLocationsDevice
);

export const selectHasLoadedAnyAppleDevice = createSelector(
  selectDeviceState,
  (state) => state.hasLoadedAnyAppleDevice
);

export const selectHasLoadedAnyInUse = createSelector(selectDeviceState, (state) => state.hasLoadedAnyInUse);

export const selectHasLoadedAnyMdmDevice = createSelector(selectDeviceState, (state) => state.hasLoadedAnyMdmDevice);

export const selectHasLoadedLocationsDevice = (udid: string) =>
  createSelector(selectDeviceEntities, (entities) => entities[udid]?.hasLoadedLocationsDevice ?? false);

export const selectHasLoadedAppleDevice = (udid: string) =>
  createSelector(selectDeviceEntities, (entities) => entities[udid]?.hasLoadedAppleDevice ?? false);

export const selectHasLoadedInUse = (udid: string) =>
  createSelector(selectDeviceEntities, (entities) => entities[udid]?.hasLoadedInUse ?? false);

export const selectHasLoadedMdmDevice = (udid: string) =>
  createSelector(selectDeviceEntities, (entities) => entities[udid]?.hasLoadedMdmDevice ?? false);

// ============================================================================
// DEVICE BY ID SELECTORS
// ============================================================================

export const selectDeviceByUdid = (udid: string) => createSelector(selectDeviceEntities, (entities) => entities[udid]);

export const selectCurrentDevice = createSelector(
  selectDeviceEntities,
  DevicesRouterStoreSelectors.selectUdid,
  (entities, udid) => (udid ? entities[udid] : undefined)
);

// ============================================================================
// DEVICE FILTERING SELECTORS
// ============================================================================

export const selectDevicesForLocation = (locationUniqueId: string) =>
  createSelector(selectAll, (devices) =>
    devices ? devices.filter((device) => device.locationCategoryId === locationUniqueId) : []
  );

export const selectLocationDevicesByState = (locationUniqueId: string, state: DeviceStateEnum) =>
  createSelector(selectAll, (devices: Device[]) =>
    devices ? devices.filter((device) => device.locationCategoryId === locationUniqueId && device.state === state) : []
  );

export const selectCurrentLocationDevices = createSelector(
  selectAll,
  LocationsRouterStoreSelectors.selectLocation,
  (devices: Device[], location: Location) =>
    devices && location ? devices.filter((device) => device.locationCategoryId === location.categoryId) : []
);

export const selectCurrentLocationDevicesByState = (state: DeviceStateEnum) =>
  createSelector(selectCurrentLocationDevices, (devices: Device[]) =>
    devices ? devices.filter((device) => device.state === state) : []
  );

export const selectCurrentLocationDevicesForSubset = (locationSubset: LocationSubset) => {
  switch (locationSubset) {
    case LocationSubset.All:
      return selectCurrentLocationDevices;
    case LocationSubset.Assigned:
      return selectCurrentLocationAssignedDevices;
    case LocationSubset.Spare:
      return selectCurrentLocationSpareDevices;
    case LocationSubset.Swapped:
      return selectCurrentLocationSwappedDevices;
    case LocationSubset.Housekeeping:
      return selectCurrentLocationHousekeepingDevices;
    case LocationSubset.Staff:
      return selectCurrentLocationStaffDevices;
    case LocationSubset.FrontDesk:
      return selectCurrentLocationFrontDeskDevices;
    case LocationSubset.Bar:
      return selectCurrentLocationBarDevices;
    case LocationSubset.Kiosk:
      return selectCurrentLocationKioskDevices;
  }
};

export const selectDevicesByState = (state: DeviceStateEnum) =>
  createSelector(selectAll, (devices: Device[]) => (devices ? devices.filter((device) => device.state === state) : []));

export const selectDevicesCountByState = (state: DeviceStateEnum) =>
  createSelector(selectAll, (devices: Device[]) =>
    devices ? devices.reduce((acc, curr) => (curr.state === state ? acc + 1 : acc), 0) : undefined
  );

export const selectDevicesForCurrentRoom = createSelector(
  selectAll,
  LocationsRouterStoreSelectors.selectRoomId,
  (devices: Device[], roomUniqueId: string) =>
    devices ? devices.filter((device) => device.roomCategoryId === roomUniqueId) : []
);

// ============================================================================
// LOCATION-SPECIFIC DEVICE SELECTORS
// ============================================================================

/** Assigned devices for current location, sorted by room display name. Returns Device[]. */
export const selectCurrentLocationAssignedDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Assigned),
  CategoryStoreSelectors.selectAllCategoryEntitiesByType('Room'),
  (devices: Device[], rooms: Room[]) =>
    devices && rooms
      ? devices
          .filter((device) => !!device.roomCategoryId && device.isLostModeEnabled === false)
          .map((device) => ({ device, room: rooms.find((r) => r.categoryId === device.roomCategoryId) }))
          .sort((a, b) =>
            a.room && b.room ? a.room.displayName.localeCompare(b.room.displayName, 'en', { numeric: true }) : 0
          )
          .map(({ device }) => device)
      : []
);

export const selectCurrentLocationDevicesCountByDeviceState = (state: DeviceStateEnum) =>
  createSelector(selectCurrentLocationDevicesByState(state), (devices: Device[]) =>
    devices ? devices.filter((device) => !device.housekeepingCategoryId).length : 0
  );

export const selectCurrentLocationSpareDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Spare),
  (devices: Device[]) => (devices ? devices.filter((device) => device.isLostModeEnabled === false) : [])
);

export const selectCurrentLocationSpareDevicesCount = createSelector(
  selectCurrentLocationSpareDevices,
  (devices: Device[]) => (devices ? devices.length : 0)
);

export const selectCurrentLocationHousekeepingDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Assigned),
  (devices: Device[]) =>
    devices ? devices.filter((device) => !!device.housekeepingCategoryId && device.isLostModeEnabled === false) : []
);

export const selectCurrentLocationHousekeepingDevicesCount = createSelector(
  selectCurrentLocationHousekeepingDevices,
  (devices: Device[]) => (devices ? devices.length : 0)
);

export const selectCurrentLocationStaffDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Assigned),
  (devices: Device[]) =>
    devices ? devices.filter((device) => !!device.staffCategoryId && device.isLostModeEnabled === false) : []
);

export const selectCurrentLocationStaffDevicesCount = createSelector(
  selectCurrentLocationStaffDevices,
  (devices: Device[]) => (devices ? devices.length : 0)
);

export const selectCurrentLocationFrontDeskDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Assigned),
  (devices: Device[]) =>
    devices ? devices.filter((device) => !!device.frontDeskCategoryId && device.isLostModeEnabled === false) : []
);

export const selectCurrentLocationFrontDeskDevicesCount = createSelector(
  selectCurrentLocationFrontDeskDevices,
  (devices: Device[]) => (devices ? devices.length : 0)
);

export const selectCurrentLocationBarDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Assigned),
  (devices: Device[]) =>
    devices ? devices.filter((device) => !!device.barCategoryId && device.isLostModeEnabled === false) : []
);

export const selectCurrentLocationBarDevicesCount = createSelector(
  selectCurrentLocationBarDevices,
  (devices: Device[]) => (devices ? devices.length : 0)
);

export const selectCurrentLocationKioskDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Assigned),
  (devices: Device[]) =>
    devices ? devices.filter((device) => !!device.kioskCategoryId && device.isLostModeEnabled === false) : []
);

export const selectCurrentLocationKioskDevicesCount = createSelector(
  selectCurrentLocationKioskDevices,
  (devices: Device[]) => (devices ? devices.length : 0)
);

export const selectCurrentLocationSwappedDevices = createSelector(
  selectCurrentLocationDevicesByState(DeviceStateEnum.Spare),
  selectCurrentLocationDevicesByState(DeviceStateEnum.Assigned),
  (spareDevices: Device[], assignedDevices: Device[]) =>
    [...(spareDevices ?? []), ...(assignedDevices ?? [])].filter((device) => device.isLostModeEnabled === true)
);

export const selectCurrentLocationSwappedDevicesCount = createSelector(
  selectCurrentLocationSwappedDevices,
  (devices: Device[]) => (devices ? devices.length : 0)
);

// ============================================================================
// DEVICE PERMISSION SELECTORS
// ============================================================================

export const selectAllowedToUnassign = createSelector(selectCurrentDevice, deviceAllowedToUnassign);
export function deviceAllowedToUnassign(device: Device | undefined) {
  return !!device && device.state !== DeviceStateEnum.Unassigned && device.state !== DeviceStateEnum.Assigned;
}

export const selectAllowedToMarkUnavailable = createSelector(selectCurrentDevice, deviceAllowedToMarkUnavailable);
export function deviceAllowedToMarkUnavailable(device: Device | undefined) {
  return !!device && device.state !== DeviceStateEnum.Unavailable;
}

export const selectAllowedToAssign = createSelector(selectCurrentDevice, deviceAllowedToAssign);
export function deviceAllowedToAssign(device: Device | undefined) {
  return !!device && device.state !== DeviceStateEnum.Assigned && device.state !== DeviceStateEnum.Unavailable;
}

export const selectAllowedToAssignToHousekeeping = createSelector(
  selectCurrentDevice,
  deviceAllowedToAssignToHousekeeping
);
export function deviceAllowedToAssignToHousekeeping(device: Device | undefined) {
  return !!device && device.state !== DeviceStateEnum.Assigned && !device.housekeepingCategoryId;
}

export const selectAllowedToAssignToStaff = createSelector(selectCurrentDevice, deviceAllowedToAssignToStaff);
export function deviceAllowedToAssignToStaff(device: Device | undefined) {
  return !!device && device.state !== DeviceStateEnum.Assigned && !device.staffCategoryId;
}

export const selectAllowedToAssignToFrontDesk = createSelector(selectCurrentDevice, deviceAllowedToAssignToFrontDesk);
export function deviceAllowedToAssignToFrontDesk(device: Device | undefined) {
  return !!device && device.state !== DeviceStateEnum.Assigned && !device.frontDeskCategoryId;
}

export const selectAllowedToAssignToBar = createSelector(selectCurrentDevice, deviceAllowedToAssignToBar);
export function deviceAllowedToAssignToBar(device: Device | undefined) {
  return !!device && device.state !== DeviceStateEnum.Assigned && !device.barCategoryId;
}

export const selectAllowedToAssignToKiosk = createSelector(selectCurrentDevice, deviceAllowedToAssignToKiosk);
export function deviceAllowedToAssignToKiosk(device: Device | undefined) {
  return !!device && device.state !== DeviceStateEnum.Assigned && !device.kioskCategoryId;
}

export const selectAllowedToMarkAsSpare = createSelector(selectCurrentDevice, deviceAllowedToMarkAsSpare);
export function deviceAllowedToMarkAsSpare(device: Device | undefined) {
  return !!device && device.state !== DeviceStateEnum.Spare;
}

// ============================================================================
// LOCATION/ROOM SELECTORS
// ============================================================================

export const selectSelectedLocationForCurrentDevice = createSelector(
  selectCurrentDevice,
  CategoryStoreSelectors.selectAllCategoryEntitiesByType('Location'),
  (device: Device | undefined, locations: Location[]) =>
    device && locations ? locations.find((location) => location.categoryId === device.locationCategoryId) : undefined
);

export const selectRoomForCurrentDevice = createSelector(
  selectCurrentDevice,
  CategoryStoreSelectors.selectAllCategoryEntitiesByType('Room'),
  (device: Device | undefined, rooms: Room[]) =>
    device && rooms ? rooms.find((room) => room.categoryId === device.roomCategoryId) : undefined
);

export const selectSelectedLocation = createSelector(
  LocationsRouterStoreSelectors.selectBrandHumanId,
  selectSelectedLocationForCurrentDevice,
  selectRoomForCurrentDevice,
  (brandId, location, room) => {
    return brandId
      ? {
          brandId,
          locationId: location ? location.id : undefined,
          roomId: room ? room.id : undefined,
          isValid: true,
        }
      : undefined;
  }
);

export const selectRoomsByLocationWithoutAnyDevice = (locationId: string) =>
  createSelector(
    CategoryStoreSelectors.selectRoomsByLocationId(locationId),
    selectAll,
    (rooms: Room[], devices: Device[]) => {
      return rooms.filter(
        (room) =>
          devices.findIndex(
            (device) => device.roomCategoryId === room.categoryId && device.locationCategoryId === locationId
          ) === -1
      );
    }
  );

// ============================================================================
// DEVICE LINK SELECTORS
// ============================================================================

export const selectCurrentDeviceLink = createSelector(selectCurrentDevice, (device) => {
  if (!device) return undefined;
  return {
    deviceId: device.udid,
    deviceType: DeviceType.Apple,
  };
});

// ============================================================================
// APPLICATION STATE SELECTORS
// ============================================================================

export const selectDeviceApplicationIsInUseByUdid = (udid: string) =>
  createSelector(selectDeviceEntities, (entities) => entities[udid]?.inUse ?? false);

// ============================================================================
// COMPATIBILITY SELECTORS (for gradual migration)
// ============================================================================

// These selectors maintain compatibility with existing code that expects
// specific types from the old stores

export const selectDeviceByCurrentAppleDevice = createSelector(selectCurrentDevice, (device) => device);
